import dedent from 'dedent';
import { ReactElement } from 'react';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import { BreFloorplanRate } from '~/tenants/bre/performable/floorplan/BreFloorplanData';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { TenantPackageAddOnCheckboxName } from '~/tenants/common/package';

interface BreFloorplanAddOnsProps {
  context: BreOrderContext;
  persona: PersonaType;
}

export default function BreFloorplanAddOns({ context, persona }: BreFloorplanAddOnsProps): ReactElement {
  const { CAD_FILE, FLOORPLAN_3D, GLA_REPORT, INTERACTIVE_TOUR } = BreFloorplanRate[context.metadata.type];

  return (
    <>
      <TenantPackageAddOnCheckboxName
        name="interactive_tour"
        title="Interactive Home Tour"
        description={dedent`
          Enhance your property listings with an interactive floor plan that gives buyers a true \
          sense of the home’s layout. This digital tool allows viewers to explore each room with \
          a simple tap, providing clear dimensions and a seamless flow of the space. Whether on a \
          desktop or mobile device, potential buyers can visualize how the home fits their lifestyle, \
          making it easier to picture themselves living there.
        `}
        image="tenant/bre/floorplan/interactive_tour.jpg"
        persona={persona}
        cost={INTERACTIVE_TOUR}
      />
      <TenantPackageAddOnCheckboxName
        name="gla_report"
        title="GLA Report"
        description={dedent`
          A Gross Living Area (GLA) report provides an accurate measurement of a property's \
          living space, including the square footage of each room and the total area. This \
          information is essential for buyers, sellers, and real estate professionals to \
          understand the property's size, layout, and value. A GLA report helps ensure that \
          property listings are accurate and transparent, providing buyers with the information \
          they need to make informed decisions.
        `}
        image="tenant/bre/floorplan/gla_report_v2.png"
        persona={persona}
        cost={GLA_REPORT}
      />
      <TenantPackageAddOnCheckboxName
        name="floorplan_3d"
        title="3D Floorplan"
        description={dedent`
          Visualizing a property in 3D gives buyers a clear understanding of its space, layout, \
          and overall design. With detailed 3D house planning, buyers can better grasp how the \
          property functions, from room flow to key features. A 3D floor plan is especially valuable \
          for showcasing properties that are vacant, under construction, or undergoing renovation, \
          helping buyers visualize their full potential and usability.
        `}
        image="tenant/bre/floorplan/floorplan_3d.png"
        persona={persona}
        cost={FLOORPLAN_3D}
      />
      <TenantPackageAddOnCheckboxName
        name="cad_file"
        title="CAD File"
        description={dedent`
          A Computer-Aided Design (CAD) file is a digital blueprint that provides a detailed, \
          scaled representation of a property's floor plan. This file is essential for architects, \
          engineers, and other professionals who need precise measurements and dimensions for \
          design, renovation, or construction projects. A CAD file is also useful for creating \
          3D models, virtual tours, and other visualizations that help buyers understand the layout \
          and potential of a property.
        `}
        image="tenant/bre/floorplan/cad_file.png"
        persona={persona}
        cost={CAD_FILE}
      />
    </>
  );
}
